import React, { useEffect, useState } from "react";
import clsx from "clsx";
import ReportStepFrame from "components/report/ReportStepFrame";
import styles from "./DomainHighlightsStep.module.scss";

export interface DomainContributor {
  /** The contributing score. */
  value: number | string;
  outOf?: number | string;
  /** What the contributor is called, supplied by the caller. */
  label: string;
}

export interface DomainHighlightTile {
  /**
   * Stable key. Never rendered. The label cannot serve as one: it is copy,
   * so it changes with the reader's language and could repeat across tiles.
   */
  id: string;
  /** The domain's score. */
  value: number | string;
  /** What it is out of. The designs show 100 throughout. */
  outOf?: number | string;
  /**
   * What to call the domain. The review note asks that domains be spoken of
   * naturally rather than by their internal names, so the caller decides:
   * this component never maps a domain key to a word.
   */
  label: string;
  /**
   * The domain's glyph. A node rather than a URL: the glyphs are stroked in
   * `currentColor` so the tile decides their colour, and an <img> would
   * render them in their own document where that resolves to black.
   */
  icon?: React.ReactNode;
  /**
   * The contributors card's heading while this tile is selected. A node, not
   * a string: the designs set the domain's glyph inside the sentence, and
   * where it falls is the translator's decision, not ours.
   */
  contributorsHeading?: React.ReactNode;
  /**
   * What underlies this domain. Each tile carries its own, because selecting
   * a tile is what reveals them.
   */
  contributors?: DomainContributor[];
}

export interface DomainHighlightsStepProps {
  /** The step's heading, e.g. the caller's wording for strengths or growth. */
  heading: string;
  /** The short account of what is driving these areas. */
  narrative: string;
  /**
   * The areas to surface, in the order they should read. The designs show
   * three; one or two render as a coherent, narrower row, because a learner
   * can be missing domain scores. The first is selected on arrival unless
   * `initialSelectedId` names another.
   */
  tiles: DomainHighlightTile[];
  /**
   * Which tile to arrive on. The narrative beside the tiles is written about
   * one domain, and the caller is the only one who knows which, so it says
   * so here rather than this component guessing from the scores. An id
   * matching no tile leaves the first selected.
   */
  initialSelectedId?: string;
  /** Names the group of tiles for assistive technology. */
  tilesLabel?: string;
  /**
   * Shown in the contributors card when the selected domain has nothing
   * under it. A domain can legitimately have no themes, and an empty list
   * beneath a heading promising contributors reads as broken.
   */
  noContributorsMessage?: string;
  /**
   * Optional line keeping a difficult result from landing as a verdict, of
   * the "X% of people score low here too" kind. Copy, so the caller owns
   * whether it appears at all.
   */
  socialProof?: string;
  /** Told a tile's `id` each time the learner picks it. */
  onSelect?: (id: string) => void;
}

/**
 * The domain-highlights step: three areas side by side, what underlies the
 * selected one, and the standard score readout.
 *
 * The screen showing what is trending well and the screen showing what needs
 * attention are the same screen. Every position in both frames matches
 * (heading, narrative, three 185x137 tiles, the contributors card below) and
 * the only differences are the words and which scores are passed in. So this
 * is one component: two copies would be two places to fix a layout bug, and
 * they would drift.
 *
 * The tiles select. Picking one swaps the card beneath, which is the whole
 * point of the screen: a domain score on its own says nothing a learner can
 * act on, and what feeds it does.
 */
const DomainHighlightsStep: React.FC<DomainHighlightsStepProps> = ({
  heading,
  narrative,
  tiles,
  initialSelectedId,
  tilesLabel,
  noContributorsMessage,
  socialProof,
  onSelect,
}) => {
  // The tile the narrative is about, or the first one, so the card is never
  // empty on arrival. Held by id rather than index so a re-ordered list
  // cannot move the selection to a different domain under the learner.
  const [selectedId, setSelectedId] = useState<string | undefined>(
    initialSelectedId ?? tiles[0]?.id
  );

  // The narrative can land after this step has mounted, which is the ordinary
  // case for a learner who arrives while their copy is still being written.
  // Moving with it is what keeps the two halves of the screen about the same
  // domain; a click afterwards is the learner's own and stands.
  useEffect(() => {
    if (initialSelectedId) setSelectedId(initialSelectedId);
  }, [initialSelectedId]);

  const selected = tiles.find((tile) => tile.id === selectedId) ?? tiles[0];
  const contributors = selected?.contributors ?? [];
  const hasCard = Boolean(
    selected && (selected.contributorsHeading || contributors.length > 0)
  );

  return (
    <ReportStepFrame>
      <div className={styles.step} data-testid="domain-highlights-step">
        <h2 className={styles.heading}>{heading}</h2>
        <p className={styles.narrative}>{narrative}</p>

        {tiles.length > 0 && (
          <ul
            className={styles.tiles}
            aria-label={tilesLabel}
            data-testid="domain-highlight-tiles"
          >
            {tiles.map((tile) => {
              const isSelected = tile.id === selected?.id;
              return (
                <li key={tile.id} className={styles["tile-item"]}>
                  <button
                    type="button"
                    className={clsx(styles.tile, isSelected && styles.selected)}
                    onClick={() => {
                      setSelectedId(tile.id);
                      onSelect?.(tile.id);
                    }}
                    // Announced as a choice among the three rather than as a
                    // toggle, which is what it is.
                    aria-pressed={isSelected}
                    data-testid="domain-highlight-tile"
                    data-selected={isSelected}
                  >
                    {tile.icon && (
                      <span
                        className={styles.icon}
                        aria-hidden="true"
                        data-testid="domain-highlight-icon"
                      >
                        {tile.icon}
                      </span>
                    )}
                    <span className={styles["tile-label"]}>{tile.label}</span>
                    <span className={styles["tile-value"]}>
                      <span className={styles["tile-number"]}>
                        {tile.value}
                      </span>
                      <span className={styles["tile-out-of"]}>
                        / {tile.outOf ?? 100}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hasCard && (
          <div
            className={styles.contributors}
            data-testid="domain-contributors"
          >
            {selected?.contributorsHeading && (
              <p className={styles["contributors-heading"]}>
                {selected.contributorsHeading}
              </p>
            )}

            {contributors.length > 0 ? (
              <ul className={styles["contributor-list"]}>
                {contributors.map((contributor, index) => (
                  <li
                    key={`${contributor.label}-${index}`}
                    className={styles.contributor}
                    data-testid="domain-contributor"
                  >
                    <span className={styles["contributor-value"]}>
                      {contributor.value}
                      <span className={styles["contributor-out-of"]}>
                        {" "}
                        / {contributor.outOf ?? 100}
                      </span>
                    </span>
                    <span className={styles["contributor-label"]}>
                      {contributor.label}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              noContributorsMessage && (
                <p
                  className={styles["contributors-empty"]}
                  data-testid="domain-contributors-empty"
                >
                  {noContributorsMessage}
                </p>
              )
            )}
          </div>
        )}

        {socialProof && (
          <p
            className={styles["social-proof"]}
            data-testid="domain-social-proof"
          >
            {socialProof}
          </p>
        )}
      </div>
    </ReportStepFrame>
  );
};

export default DomainHighlightsStep;
